import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pri-dark to-sec-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="flex justify-center mb-4">
              <DocumentTextIcon className="h-16 w-16 text-mint-green" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-soft-pink">Terms of Service</h1>
            <div className="h-1 w-24 bg-mint-green mx-auto mb-6"></div>
            <p className="text-pri-gray text-lg max-w-2xl mx-auto">
              Last updated: April 20, 2025
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none prose-headings:text-mint-green prose-p:text-pri-gray prose-strong:text-white prose-hr:border-white/10"
          >
            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Agreement to Terms</h2>
              <p className="text-pri-gray mb-4">
                These Terms of Service govern your use of the Jingleez platform and website operated by Jingleez Team.
              </p>
              <p className="text-pri-gray mb-4">
                By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service.
              </p>
              <p className="text-pri-gray">
                As we are currently in beta, these terms may change as we continue to develop and improve our platform. We will notify users of any significant changes to these terms.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Use of the Service</h2>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Account Registration</h3>
              <p className="text-pri-gray mb-4">
                To use certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </p>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Account Responsibilities</h3>
              <p className="text-pri-gray mb-4">
                You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your account. You agree not to disclose your password to any third party.
              </p>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Acceptable Use</h3>
              <p className="text-pri-gray mb-4">
                You agree not to use the Service:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>In any way that violates any applicable national or international law or regulation</li>
                <li>For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way</li>
                <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail", "chain letter", or "spam"</li>
                <li>To impersonate or attempt to impersonate Jingleez, a Jingleez employee, another user, or any other person or entity</li>
                <li>In any way that infringes upon the rights of others, or in any way is illegal, threatening, fraudulent, or harmful</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Content and Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">User Content</h3>
              <p className="text-pri-gray mb-4">
                Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material. By providing User Content, you grant us the right and license to use, modify, perform, display, reproduce, and distribute such material within the Service.
              </p>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Jingleez Content</h3>
              <p className="text-pri-gray mb-4">
                The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of Jingleez and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Jingleez.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Termination</h2>
              <p className="text-pri-gray mb-4">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p className="text-pri-gray mb-4">
                Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service, or notify us that you wish to delete your account.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Limitation of Liability</h2>
              <p className="text-pri-gray mb-4">
                In no event shall Jingleez, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>Your access to or use of or inability to access or use the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use or alteration of your transmissions or content</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Disclaimer</h2>
              <p className="text-pri-gray mb-4">
                Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement or course of performance.
              </p>
              <p className="text-pri-gray mb-4">
                Jingleez does not warrant that a) the Service will function uninterrupted, secure or available at any particular time or location; b) any errors or defects will be corrected; c) the Service is free of viruses or other harmful components; or d) the results of using the Service will meet your requirements.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Contact Us</h2>
              <p className="text-pri-gray mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-pri-gray">
                <a href="mailto:legal@jingleez.com" className="text-mint-green hover:text-mint-green/80 transition-colors">contact@jingleez.com</a>
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-8 mb-4">
            <Link to="/terms" className="text-mint-green">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-pri-gray hover:text-mint-green transition-colors">
              Privacy Policy
            </Link>
          </div>
          <p className="text-pri-gray">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
} 